import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import {
  ArrowLeft,
  ArrowRight,
  Cancel,
  Edit,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { alpha, useTheme } from "@mui/material/styles";


import {
  HelpText,
  NewSelectionCard,
  HelpBox,
  SelectionCard,
  TitleText,
  VariableDirectory,
  VariableDiscover,
  VariableSearch,
  QueryGroupOperator,
} from "@/components/atoms";
import { BuildAudience, Variable } from "@/types";
import { useAudienceContext } from "@/context";
import { buildTabOptions, getSelectionSummary, getFirstWord } from "@/utils";
import { useClient } from "@/hooks";

interface SelectionStepProps {
  variables: Variable[];
  audience: BuildAudience;
  disabled: boolean;
}


const SelectionStep: React.FC<SelectionStepProps> = ({
  variables,
  audience,
  disabled
}) => {
  const { dispatch } = useAudienceContext();
  const client = useClient();
  const numQueryGroups = audience.var_groups.length;
  const hasVars = audience.var_groups[0].variables.length > 0; // TODO

  const [activeGroup, setActiveGroup] = useState<number|null>(disabled ? null : hasVars ? null : 1);
  const [activeTab, setActiveTab] = useState<number>(0);

  const carouselRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const summaryText = getSelectionSummary(audience.var_groups, audience.operator)

  const checkScrollPosition = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setShowLeftArrow(scrollLeft > 10);
      // Show right arrow if there is more content hidden to the right (minus a small pixel buffer)
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const carouselWidth = useMemo(() => {
    const numCards = audience.var_groups.length ;
    return 8 + (numCards * 280) + ((numCards - 1) * 16)
  }, [audience.var_groups]);

  useEffect(() => {
    const el = carouselRef.current;
    if (el) {
      el.addEventListener('scroll', checkScrollPosition);
      // Run once on load/mount to initialize arrow states
      checkScrollPosition();
    }
    return () => el?.removeEventListener('scroll', checkScrollPosition);
  }, [audience.var_groups]); // Re-evaluate when cards are added or dropped

  const scroll = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      // Moves the viewport by roughly one card's width plus gap per click
      const scrollAmount = direction === 'left' ? -340 : 340;
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const selectedVariables = useMemo(() => {
    return new Set(audience.var_groups.flatMap(x => x.variables.map(v => v.tag_id)))
  }, [audience.var_groups]);

  const activeVariables = useMemo(() => {
    const group  = audience.var_groups.find(x => x.id === activeGroup);
    return new Set(group ? group.variables.map(v => v.tag_id) : []);
  }, [audience.var_groups, activeGroup]);

  const handleAddGroup = () => {
    dispatch({ type: "ADD_VARIABLE_GROUP" });
    setActiveGroup(prev => prev ? prev + 1 : numQueryGroups + 1);
  };

  const handleRemoveGroup = (groupId: number) => {
    if (activeGroup === groupId) {
      setActiveGroup(null);
    } else if (activeGroup && activeGroup > groupId) {
      setActiveGroup(prev => prev ? prev - 1 : prev);
    }
  }

  const handleSelectVariables = (variables: Variable[]) => {
    const group = audience.var_groups.find(x => x.id === activeGroup);
    const groupVars = group ? group.variables : [];
    activeGroup && dispatch({ type: "CHANGE_GROUP_VARIABLES", payload: { id: activeGroup, variables: [...groupVars, ...variables] } });
  }

  const handleDeleteVariable = (variable: Variable) => {
    const group = audience.var_groups.find(x => x.id === activeGroup);
    const update = group ? group.variables.filter((v) => v.tag_id !== variable.tag_id) : [];
    activeGroup && dispatch({ type: "CHANGE_GROUP_VARIABLES", payload: { id: activeGroup, variables: update } });
  };

  const handleChangeOperator = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (audience.operator === event.target.value) return;
    dispatch({ type: "CHANGE_OPERATOR", payload: event.target.value });
  };

  const handleGroupClick = (id: number) => {
    setActiveGroup(prev => prev === id ? null : id);
  }

  return (
    <Grid size={12}>
      <Stack spacing={2}>
        <TitleText
          text="Who are your Audience?"
          subtext="Describe their behaviour by what they buy, where they browse the web, when they travel, or how they describe themselves. Select a query group to make it 'Active', then add variables"
          variant="body1"
          spacing={0.5}
        />

        {/*Outer Box*/}
        <Box>

        {/* Carousel Box */}
        <Box sx={{ position: 'relative', width: '100%' }}>

          {/* Left Nav Button */}
          {showLeftArrow && (
            <IconButton
              onClick={() => scroll('left')}
              sx={{
                position: 'absolute',
                left: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10, // Sits comfortably above cards
                bgcolor: 'background.paper',
                boxShadow: 3,
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'grey.100' }
              }}
            >
              <ArrowLeft />
            </IconButton>
          )}

          {/* Right Nav Button */}
          {showRightArrow && (
            <IconButton
              onClick={() => scroll('right')}
              sx={{
                position: 'absolute',
                right: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: 'background.paper',
                boxShadow: 3,
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'grey.100' }
              }}
            >
              <ArrowRight />
            </IconButton>
          )}

          {/* Carousel */}
          <Box
            ref={carouselRef}
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 2,
              overflowX: 'auto',
              px: 1,
              pb: 0,
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              width: "100%"
            }}
          >
            {audience.var_groups.map((group, index) => (
              <React.Fragment key={`var-group-${index}`}>
                <SelectionCard
                  variables={variables}
                  varGroup={group}
                  numQueryGroups={numQueryGroups}
                  active={group.id === activeGroup}
                  onClick={() => handleGroupClick(group.id)}
                  onRemove={handleRemoveGroup}
                  disabled={disabled}
                />
              </React.Fragment>
            ))}
            {!disabled && <NewSelectionCard onClick={handleAddGroup}/>}
          </Box>
        </Box>

          {activeGroup ? (
            // Variable Panel
            <Paper
              elevation={2}
              sx={{
                height: 641,
                mx: 1,
                mt: 2,
                p: 2,
                borderRadius: "6px",
                border: 1,
                borderColor: "secondary.main",
              }}
            >

              {/* Header Tabs */}
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 2,
                  py: 1.5,
                  mt: -2,
                  mx: -2,
                  mb: 1,
                  borderRadius: "6px 6px 0px 0px",
                  borderBottom: 1,
                  borderColor: "divider"
              }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  TabIndicatorProps={{ style: { display: "none" } }}
                  sx={{
                    minHeight: 36,
                    "& .MuiTabs-flexContainer": {
                      justifyContent: "flex-start",
                      alignItems: "flex-end",
                      gap: 1,
                    }
                  }}
                >
                  {buildTabOptions.map((tab, index) => {
                    const isSelected = activeTab === index;

                    return (
                      <Tab
                        key={tab.label}
                        icon={tab.icon}
                        iconPosition="start"
                        label={tab.label}
                        sx={{
                          minHeight: 36,
                          height: 36,
                          py: 0,
                          px: 2,
                          minWidth: "auto",
                          pointerEvents: 'auto !important',
                          opacity: '1 !important',
                          cursor: 'pointer !important',
                          lineHeight: "normal",
                          transition: "all 0.15s ease-in-out",
                          borderRadius: "6px",
                          // border: isSelected ? "0px": "1px solid",
                          // borderColor: isSelected ? "transparent" : "text.secondary",
                          backgroundColor: isSelected ? "secondary.main" : "transparent",
                          color: isSelected ? "secondary.contrastText" : "text.secondary",
                          "&:hover": {
                            backgroundColor: isSelected ? "secondary.main" : "action.hover",
                          },
                          "&.Mui-selected": {
                            color: "secondary.contrastText"
                          }
                        }}
                      />
                    )
                  })}
                </Tabs>
                <HelpBox title="Close Variable Selection">
                  <IconButton onClick={() => setActiveGroup(null)} sx={{ mr: -1 }}>
                    <Cancel />
                  </IconButton>
                </HelpBox>
              </Box>

              {/* Views */}
              <Box>
                {/* Taxonomy View */}
                <Box sx={{ display: activeTab === 0 ? "block" : "none" }}>
                  <VariableDirectory
                    variables={variables}
                    onSelect={handleSelectVariables}
                    onDelete={handleDeleteVariable}
                    selected={selectedVariables}
                    active={activeVariables}
                  />
                </Box>

                {/* Search View */}
                <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
                  <VariableSearch
                    variables={variables}
                    onSelect={handleSelectVariables}
                    onDelete={handleDeleteVariable}
                    selected={selectedVariables}
                    active={activeVariables}
                  />
                </Box>

                {/* AI View */}
                {client === 'core' && (
                  <Box sx={{ display: activeTab === 2 ? "block" : "none" }}>
                    <VariableDiscover
                      variables={variables}
                      onSelect={handleSelectVariables}
                      onDelete={handleDeleteVariable}
                      selected={selectedVariables}
                      active={activeVariables}
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          ) : (

            <>
              {/*Operator*/}
              {audience.var_groups.length > 1 && (
                <Box
                  sx={{
                    width: `min(${carouselWidth}px, 100%)`,
                    px: 4,
                    pt: 2,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1, // Grows to fill all space from left card to dropdown edge
                      height: '10px', // Matches your bracket drop depth
                      border: '2px solid',
                      borderColor: 'divider',
                      borderTop: 'none',
                      borderRight: 'none', // Leave the right side open for the dropdown
                      borderRadius: '0 0 0 4px', // Round only the outer bottom-left corner
                    }}
                  />
                  <QueryGroupOperator
                    value={audience.operator}
                    onChange={handleChangeOperator}
                    disabled={disabled}
                  />
                  <Box
                    sx={{
                      flexGrow: 1, // Grows to fill all space from dropdown edge to right card
                      height: '10px',
                      border: '2px solid',
                      borderColor: 'divider',
                      borderTop: 'none',
                      borderLeft: 'none', // Leave the left side open for the dropdown
                      borderRadius: '0 0 4px 0', // Round only the outer bottom-right corner
                    }}
                  />
                </Box>
              )}

              {/*Summary*/}
              <Paper
                elevation={2}
                sx={{
                  width: `min(max(${carouselWidth}px, 624px), 100%)`,
                  p: 2,
                  pt: 1,
                  ml: 1,
                  mt: 2,
                  backgroundColor: "background.paper",
                  borderRadius: "6px"
                }}
              >
                <Stack>
                  <Typography>
                    Your Audience
                  </Typography>
                  <Typography variant="body2">
                    <Typography variant="body2" component="span">
                      {getFirstWord(summaryText) === 'interested' ? "People who are " : "People who "}
                    </Typography>
                    {summaryText && Array.isArray(summaryText) ? (
                    summaryText
                    ) : (
                    <Typography variant="body2" color="text.primary" sx={{ fontWeight: 'bold' }}>
                      {summaryText}
                    </Typography>
                    )}
                  </Typography>
                </Stack>
              </Paper>
            </>
          )}

        </Box>

      </Stack>
    </Grid>
  )
}

export default SelectionStep;
